import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import TextArea from '../components/common/TextArea'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { companyApi } from '../services/api'
import MoveButtons from '../components/common/MoveButtons'
import { refreshPlates } from '../hooks/usePlates'

export default function CompanySettings() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState({
        companyName: '',
        addressLine1: '',
        addressLine2: '',
        phone: '',
        fax: '',
        website: '',
        rightLogoText: '',
        shopJobsite: '',
        pdfExportPath: '',
        gstHstNumber: '' 
    })
    const [hasLeftLogo, setHasLeftLogo] = useState(false)
    const [hasRightLogo, setHasRightLogo] = useState(false)

    // The vehicle plates behind the picker on a fuel expense. Edited as rows
    // here and saved with everything else on the page.
    const [plates, setPlates] = useState([])

    useEffect(() => {
        loadSettings()
    }, [])

    async function loadSettings() {
        try {
            const data = await companyApi.getSettings()
            if (data.success) {
                setForm({
                    companyName: data.settings.companyName || '',
                    addressLine1: data.settings.addressLine1 || '',
                    addressLine2: data.settings.addressLine2 || '',
                    phone: data.settings.phone || '',
                    fax: data.settings.fax || '',
                    website: data.settings.website || '',
                    rightLogoText: data.settings.rightLogoText || '',
                    shopJobsite: data.settings.shopJobsite || '',
                    pdfExportPath: data.settings.pdfExportPath || '',
                    gstHstNumber: data.settings.gstHstNumber || ''
                })
                setHasLeftLogo(data.settings.hasLeftLogo)
                setHasRightLogo(data.settings.hasRightLogo)
            }

            const platesData = await companyApi.getPlates()
            if (platesData.success) {
                setPlates((platesData.plates || []).map(p => ({
                    plate: p.plate || '',
                    description: p.description || '',
                })))
            }
        } catch (error) {
            toast.error('Error loading settings')
        }
        setLoading(false)
    }

    function addPlate() {
        setPlates(prev => [...prev, { plate: '', description: '' }])
    }

    function changePlate(index, field, value) {
        setPlates(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)))
    }

    function removePlate(index) {
        setPlates(prev => prev.filter((_, i) => i !== index))
    }

    // Held until Save, unlike the arrows on the categories screen: everything
    // else on this page works that way, and a row that saved itself while the
    // form around it had not would be the odd one out.
    function movePlate(index, direction) {
        const target = index + direction
        if (target < 0 || target >= plates.length) return

        setPlates(prev => {
            const next = [...prev]
            const [moved] = next.splice(index, 1)
            next.splice(target, 0, moved)
            return next
        })
    }

    async function handleSave() {
        // Blank rows are just an "Add plate" someone thought better of, so they
        // are dropped rather than complained about. A duplicate is worth saying:
        // the server would silently keep one, and the row that vanished would
        // look like a bug.
        const entered = plates
            .map(p => ({ plate: p.plate.trim().toUpperCase(), description: p.description.trim() }))
            .filter(p => p.plate)

        const duplicate = entered.find((p, i) => entered.findIndex(o => o.plate === p.plate) !== i)
        if (duplicate) {
            toast.error(`${duplicate.plate} is on the plate list twice`)
            return
        }

        setSaving(true)
        try {
            const data = await companyApi.updateSettings(form)
            if (!data.success) {
                toast.error(data.message || 'Failed to save')
                setSaving(false)
                return
            }

            const platesData = await companyApi.updatePlates(entered)
            if (!platesData.success) {
                // The company details did save, so say what did and did not.
                toast.error(platesData.message || 'Settings saved, but the plate list could not be')
                setSaving(false)
                return
            }

            // Every picker on the site reads a cached copy of this list.
            await refreshPlates()

            toast.success('Settings saved')
            navigate('/sync')
        } catch (error) {
            toast.error('Error saving settings')
        }
        setSaving(false)
    }

    async function handleLogoUpload(side, file) {
        if (!file) return
        try {
            const data = await companyApi.uploadLogo(side, file)
            if (data.success) {
                toast.success(`${side === 'left' ? 'Left' : 'Right'} logo uploaded`)
                if (side === 'left') setHasLeftLogo(true)
                else setHasRightLogo(true)
            } else {
                toast.error(data.message || 'Upload failed')
            }
        } catch (error) {
            toast.error('Error uploading logo')
        }
    }

    async function handleLogoDelete(side) {
        if (!confirm(`Delete ${side} logo?`)) return
        try {
            const data = await companyApi.deleteLogo(side)
            if (data.success) {
                toast.success(`${side === 'left' ? 'Left' : 'Right'} logo deleted`)
                if (side === 'left') setHasLeftLogo(false)
                else setHasRightLogo(false)
            } else {
                toast.error(data.message || 'Delete failed')
            }
        } catch (error) {
            toast.error('Error deleting logo')
        }
    }

    function handleChange(field, value) {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    return (
        <div>
            <div className="page-header flex items-center justify-between">
                <h1 className="page-title">Company Information</h1>
                <Button variant="secondary" onClick={() => navigate('/sync')}>
                    ← Back to Sync Dashboard
                </Button>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
                {/* Company Details */}
                <h2 className="text-lg font-semibold mb-4">Company Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <Input
                        label="Company Name"
                        value={form.companyName}
                        onChange={(e) => handleChange('companyName', e.target.value)}
                    />
                    <Input
                        label="Address Line 1"
                        value={form.addressLine1}
                        onChange={(e) => handleChange('addressLine1', e.target.value)}
                        placeholder="e.g. 5617 Line 7 North"
                    />
                    <Input
                        label="Address Line 2"
                        value={form.addressLine2}
                        onChange={(e) => handleChange('addressLine2', e.target.value)}
                        placeholder="e.g. Oro Medonte, ON, L0K1N0"
                    />
                    <Input
                        label="Phone"
                        value={form.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                    />
                    <Input
                        label="Fax"
                        value={form.fax}
                        onChange={(e) => handleChange('fax', e.target.value)}
                    />
                    <Input
                        label="Website"
                        value={form.website}
                        onChange={(e) => handleChange('website', e.target.value)}
                    />
                </div>

                {/* PDF Settings */}
                <h2 className="text-lg font-semibold mt-8 mb-4">PDF Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <Input
                        label="Repair In Shop / Internal Job Site"
                        value={form.shopJobsite}
                        onChange={(e) => handleChange('shopJobsite', e.target.value)}
                        placeholder="e.g. J&T Shop"
                    />
                    <Input
                        label="PDF Export Path"
                        value={form.pdfExportPath}
                        onChange={(e) => handleChange('pdfExportPath', e.target.value)}
                        placeholder={String.raw`e.g. C:\JT Jobs`}
                    />
                    <Input
                        label="GST/HST Number"
                        value={form.gstHstNumber}
                        onChange={(e) => handleChange('gstHstNumber', e.target.value)}
                        placeholder="e.g. 1234567890"
                    />
                </div>

                {/* Vehicle Plates */}
                <h2 className="text-lg font-semibold mt-8 mb-4">Vehicle Plates</h2>
                <p className="text-sm text-gray-500 mb-3">
                    The plates offered on a fuel expense, in the order they
                    appear on the picker. Removing one takes it off the picker — it does not change
                    anything already filed, and anyone can still type a plate that is not on the list.
                </p>

                <div className="space-y-2 max-w-2xl">
                    {plates.map((plate, index) => (
                        <div key={index} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={plate.plate}
                                onChange={(e) => changePlate(index, 'plate', e.target.value.toUpperCase())}
                                placeholder="ABCD123"
                                maxLength={20}
                                className="font-mono"
                                style={{ textTransform: 'uppercase', maxWidth: '10rem' }}
                            />
                            <input
                                type="text"
                                value={plate.description}
                                onChange={(e) => changePlate(index, 'description', e.target.value)}
                                placeholder="Which truck (optional)"
                                maxLength={80}
                            />
                            <MoveButtons
                                index={index}
                                count={plates.length}
                                onMove={movePlate}
                            />
                            <button
                                type="button"
                                onClick={() => removePlate(index)}
                                className="text-red-500 hover:text-red-700 px-2 text-lg leading-none shrink-0"
                                title="Remove this plate"
                            >
                                ✕
                            </button>
                        </div>
                    ))}

                    {plates.length === 0 && (
                        <p className="text-sm text-gray-400">No plates yet.</p>
                    )}
                </div>

                <button
                    type="button"
                    onClick={addPlate}
                    className="mt-3 text-rdi-blue font-medium hover:underline"
                >
                    + Add plate
                </button>

                {/* Logos */}
                <h2 className="text-lg font-semibold mt-8 mb-4">PDF Logos</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Logo */}
                    <div className="border rounded-lg p-4">
                        <label className="form-label mb-2 block">Left Logo (Main Company Logo)</label>
                        {hasLeftLogo && (
                            <div className="mb-3 bg-gray-50 p-3 rounded flex items-center justify-between">
                                <img
                                    src={`/api/company-settings/logo/left?t=${Date.now()}`}
                                    alt="Left Logo"
                                    className="max-h-20"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                                <button
                                    onClick={() => handleLogoDelete('left')}
                                    className="text-red-500 hover:text-red-700 text-sm font-medium ml-4"
                                >
                                    Delete
                                </button>
                            </div>
                        )}
                        <input
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            onChange={(e) => handleLogoUpload('left', e.target.files[0])}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="text-xs text-gray-400 mt-1">PNG or JPG, recommended ~300px wide</p>
                    </div>

                    {/* Right Logo */}
                    <div className="border rounded-lg p-4">
                        <label className="form-label mb-2 block">Right Logo (Optional Badge/Certification)</label>
                        {hasRightLogo && (
                            <div className="mb-3 bg-gray-50 p-3 rounded flex items-center justify-between">
                                <img
                                    src={`/api/company-settings/logo/right?t=${Date.now()}`}
                                    alt="Right Logo"
                                    className="max-h-20"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                                <button
                                    onClick={() => handleLogoDelete('right')}
                                    className="text-red-500 hover:text-red-700 text-sm font-medium ml-4"
                                >
                                    Delete
                                </button>
                            </div>
                        )}
                        <input
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            onChange={(e) => handleLogoUpload('right', e.target.files[0])}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="text-xs text-gray-400 mt-1">PNG or JPG, recommended ~150px wide</p>
                    </div>
                </div>

                {/* Right Logo Text */}
                <div className="mt-4">
                    <TextArea
                        label="Text Under Right Logo (optional, e.g. Licence No.)"
                        value={form.rightLogoText}
                        onChange={(e) => handleChange('rightLogoText', e.target.value)}
                        rows={2}
                        maxLength={500}
                    />
                </div>

                {/* Save */}
                <div className="flex gap-3 mt-6 pt-4 border-t">
                    <Button onClick={handleSave} loading={saving}>
                        Save Settings
                    </Button>
                </div>
            </div>
        </div>
    )
}